import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Users, Mail, LogOut, Plus, Pencil, Trash2, Send, ExternalLink, UserCog, Settings as SettingsIcon, CalendarClock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const emptyPost = { title: "", excerpt: "", content: "", category: "Cashflow", cover_image: "", read_time: "5 min read", published: true };
const emptyMember = { name: "", role: "", bio: "", photo: "", order: 0 };
const inputCls = "w-full bg-card border border-border focus:border-primary px-4 py-3 outline-none transition-colors text-sm";
const labelCls = "block font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const { settings, refresh: refreshSettings } = useSettings();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ posts: 0, published: 0, subscribers: 0, newsletters: 0, bookings: 0 });
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [bookings, setBookings] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [nl, setNl] = useState({ subject: "", html_content: "" });
  const [sending, setSending] = useState(false);

  const [team, setTeam] = useState([]);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState(emptyMember);

  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (settings) {
      setSettingsForm({
        cta_label: settings.cta_label || "",
        cta_url: settings.cta_url || "",
        group_photo: settings.group_photo || "",
        group_photo_caption: settings.group_photo_caption || "",
        about_photo: settings.about_photo || "",
        socials: {
          website: "", whatsapp: "", instagram: "", youtube: "", linkedin: "", twitter: "", facebook: "",
          ...(settings.socials || {}),
        },
      });
    }
  }, [settings]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", settingsForm);
      toast.success("Settings saved");
      refreshSettings();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const loadAll = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/posts?include_drafts=true").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/subscribers").then((r) => setSubs(r.data.subscribers || [])).catch(() => {});
    api.get("/newsletters").then((r) => setNewsletters(r.data)).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    loadAll();
  }, [user, loading, navigate, loadAll]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-background font-mono-accent text-sm text-muted-foreground">Loading control room...</div>;
  }

  const openCreate = () => { setEditing(null); setForm(emptyPost); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setDialogOpen(true); };

  const savePost = async () => {
    if (!form.title || !form.content) { toast.error("Title and content are required"); return; }
    try {
      if (editing) {
        await api.put(`/posts/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await api.post("/posts", form);
        toast.success("Post published");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/posts/${p.id}`); toast.success("Post deleted"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openCreateMember = () => { setEditingMember(null); setMemberForm({ ...emptyMember, order: team.length + 1 }); setMemberDialogOpen(true); };
  const openEditMember = (m) => { setEditingMember(m); setMemberForm({ ...m }); setMemberDialogOpen(true); };

  const saveMember = async () => {
    if (!memberForm.name) { toast.error("Name is required"); return; }
    const payload = { ...memberForm, order: Number(memberForm.order) || 0 };
    try {
      if (editingMember) {
        await api.put(`/team/${editingMember.id}`, payload);
        toast.success("Member updated");
      } else {
        await api.post("/team", payload);
        toast.success("Member added");
      }
      setMemberDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    try { await api.delete(`/team/${m.id}`); toast.success("Member removed"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Delete booking from ${b.name}?`)) return;
    try { await api.delete(`/bookings/${b.id}`); toast.success("Booking deleted"); loadAll(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendNewsletter = async () => {
    if (!nl.subject || !nl.html_content) { toast.error("Subject and content are required"); return; }
    if (!window.confirm(`Send "${nl.subject}" to ${stats.subscribers} subscriber(s)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/send", nl);
      toast.success(`Sent to ${data.sent} of ${data.total} subscribers`);
      setNl({ subject: "", html_content: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const statCards = [
    { label: "Bookings", value: stats.bookings, icon: CalendarClock },
    { label: "Total Posts", value: stats.posts, icon: FileText },
    { label: "Subscribers", value: stats.subscribers, icon: Users },
    { label: "Newsletters Sent", value: stats.newsletters, icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b border-border sticky top-0 z-40 bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="font-display font-black uppercase tracking-tighter text-lg leading-none">
            Control Room <span className="text-primary">/</span> <span className="font-mono-accent text-xs tracking-normal text-muted-foreground normal-case">Design My Cashflow</span>
          </div>
          <div className="flex items-center gap-4">
            <a data-testid="admin-view-site" href="/" target="_blank" rel="noreferrer" className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5">
              View site <ExternalLink size={12} />
            </a>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/login"); }} className="inline-flex items-center gap-2 border border-border px-3 py-2 font-mono-accent text-[11px] uppercase tracking-[0.15em] hover:border-primary hover:text-primary transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-10">
          {statCards.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`} className="bg-background p-6">
              <s.icon size={18} className="text-primary mb-4" />
              <p className="font-display font-black text-4xl leading-none">{s.value}</p>
              <p className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-muted-foreground mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="posts">
          <TabsList data-testid="admin-tabs" className="bg-card rounded-none border border-border p-0 h-auto">
            <TabsTrigger data-testid="tab-posts" value="posts" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono-accent text-xs uppercase tracking-[0.15em] px-6 py-3">Posts</TabsTrigger>
            <TabsTrigger data-testid="tab-bookings" value="bookings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono-accent text-xs uppercase tracking-[0.15em] px-6 py-3">Bookings</TabsTrigger>
            <TabsTrigger data-testid="tab-team" value="team" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono-accent text-xs uppercase tracking-[0.15em] px-6 py-3">Team</TabsTrigger>
            <TabsTrigger data-testid="tab-subscribers" value="subscribers" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono-accent text-xs uppercase tracking-[0.15em] px-6 py-3">Subscribers</TabsTrigger>
            <TabsTrigger data-testid="tab-newsletter" value="newsletter" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono-accent text-xs uppercase tracking-[0.15em] px-6 py-3">Newsletter</TabsTrigger>
            <TabsTrigger data-testid="tab-settings" value="settings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-mono-accent text-xs uppercase tracking-[0.15em] px-6 py-3">Settings</TabsTrigger>
          </TabsList>

          {/* POSTS */}
          <TabsContent value="posts" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl">Journal Posts</h2>
              <button data-testid="new-post-button" onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#1AE39C] transition-colors">
                <Plus size={16} /> New Post
              </button>
            </div>
            <div className="border border-border">
              {posts.length === 0 && <p className="p-6 font-mono-accent text-sm text-muted-foreground">No posts yet.</p>}
              {posts.map((p) => (
                <div key={p.id} data-testid={`post-row-${p.id}`} className="flex items-center gap-4 p-4 border-b border-border last:border-b-0 hover:bg-card transition-colors">
                  <div className="w-16 h-12 overflow-hidden border border-border shrink-0 hidden sm:block">
                    {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold truncate">{p.title}</p>
                    <p className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-muted-foreground mt-1">
                      {p.category} · {p.published ? "Published" : "Draft"}
                    </p>
                  </div>
                  <button data-testid={`edit-post-${p.id}`} onClick={() => openEdit(p)} className="p-2 border border-border hover:border-primary hover:text-primary transition-colors"><Pencil size={15} /></button>
                  <button data-testid={`delete-post-${p.id}`} onClick={() => deletePost(p)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-8">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mb-6">1-2-1 Consultation Requests ({bookings.length})</h2>
            <div className="border border-border">
              {bookings.length === 0 && <p className="p-6 font-mono-accent text-sm text-muted-foreground">No booking requests yet.</p>}
              {bookings.map((b) => (
                <div key={b.id} data-testid={`booking-row-${b.id}`} className="flex items-start gap-4 p-5 border-b border-border last:border-b-0 hover:bg-card transition-colors">
                  <div className="w-10 h-10 border border-border shrink-0 flex items-center justify-center bg-secondary">
                    <CalendarClock size={16} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <p className="font-display font-bold">{b.name}</p>
                      <span className="font-mono-accent text-[11px] text-primary">{b.phone}</span>
                      {b.email && <span className="font-mono-accent text-[11px] text-muted-foreground">{b.email}</span>}
                    </div>
                    <p className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-amber mt-1.5">
                      {b.preferred_date || "No date"} · {b.preferred_time || "Any slot"}
                    </p>
                    {b.message && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{b.message}</p>}
                  </div>
                  <span className="font-mono-accent text-[11px] text-muted-foreground hidden sm:block">{(b.created_at || "").slice(0, 10)}</span>
                  <button data-testid={`delete-booking-${b.id}`} onClick={() => deleteBooking(b)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TEAM */}
          <TabsContent value="team" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl">Team Members</h2>
              <button data-testid="new-member-button" onClick={openCreateMember} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#1AE39C] transition-colors">
                <Plus size={16} /> Add Member
              </button>
            </div>
            <div className="border border-border">
              {team.length === 0 && <p className="p-6 font-mono-accent text-sm text-muted-foreground">No team members yet.</p>}
              {team.map((m) => (
                <div key={m.id} data-testid={`member-row-${m.id}`} className="flex items-center gap-4 p-4 border-b border-border last:border-b-0 hover:bg-card transition-colors">
                  <div className="w-12 h-12 border border-border shrink-0 flex items-center justify-center bg-secondary overflow-hidden">
                    {m.photo ? <img src={m.photo} alt="" className="w-full h-full object-cover" /> : <UserCog size={18} className="text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold truncate">{m.name}</p>
                    <p className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-amber mt-1">{m.role || "—"}</p>
                  </div>
                  <span className="font-mono-accent text-[11px] text-muted-foreground hidden sm:block">#{m.order}</span>
                  <button data-testid={`edit-member-${m.id}`} onClick={() => openEditMember(m)} className="p-2 border border-border hover:border-primary hover:text-primary transition-colors"><Pencil size={15} /></button>
                  <button data-testid={`delete-member-${m.id}`} onClick={() => deleteMember(m)} className="p-2 border border-border hover:border-destructive hover:text-destructive transition-colors"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* SUBSCRIBERS */}
          <TabsContent value="subscribers" className="mt-8">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mb-6">Subscribers ({subs.length})</h2>
            <div className="border border-border">
              {subs.length === 0 && <p className="p-6 font-mono-accent text-sm text-muted-foreground">No subscribers yet.</p>}
              {subs.map((s, i) => (
                <div key={i} data-testid={`subscriber-row-${i}`} className="flex items-center justify-between p-4 border-b border-border last:border-b-0">
                  <div>
                    <p className="font-medium">{s.email}</p>
                    {s.name && <p className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-muted-foreground mt-1">{s.name}</p>}
                  </div>
                  <span className="font-mono-accent text-[11px] text-muted-foreground">{(s.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* NEWSLETTER */}
          <TabsContent value="newsletter" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mb-6">Compose Broadcast</h2>
                <label className={labelCls}>Subject</label>
                <input data-testid="newsletter-subject" value={nl.subject} onChange={(e) => setNl({ ...nl, subject: e.target.value })} className={`${inputCls} mb-5`} placeholder="This week in cashflow..." />
                <label className={labelCls}>Content (HTML supported)</label>
                <textarea data-testid="newsletter-content" value={nl.html_content} onChange={(e) => setNl({ ...nl, html_content: e.target.value })} rows={10} className={`${inputCls} mb-5 resize-y`} placeholder="<p>Hello investors...</p>" />
                <button data-testid="send-newsletter-button" onClick={sendNewsletter} disabled={sending} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase text-sm px-6 py-3 hover:bg-[#1AE39C] transition-colors disabled:opacity-60">
                  <Send size={15} /> {sending ? "Sending..." : `Send to ${stats.subscribers} subscribers`}
                </button>
              </div>
              <div>
                <h3 className="font-display font-extrabold uppercase tracking-tight text-lg mb-6">History</h3>
                <div className="border border-border">
                  {newsletters.length === 0 && <p className="p-4 font-mono-accent text-xs text-muted-foreground">No broadcasts yet.</p>}
                  {newsletters.map((n, i) => (
                    <div key={i} data-testid={`newsletter-history-${i}`} className="p-4 border-b border-border last:border-b-0">
                      <p className="font-medium text-sm truncate">{n.subject}</p>
                      <p className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-primary mt-1">Sent {n.sent} · Failed {n.failed}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-8">
            {settingsForm && (
              <div className="max-w-3xl space-y-10">
                <div>
                  <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mb-1">Call-to-Action Button</h2>
                  <p className="text-muted-foreground text-sm mb-6">The "Design My Cashflow Now" button in the header and CTA band redirects here.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Button Label</label>
                      <input data-testid="settings-cta-label" value={settingsForm.cta_label} onChange={(e) => setSettingsForm({ ...settingsForm, cta_label: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Redirect URL</label>
                      <input data-testid="settings-cta-url" value={settingsForm.cta_url} onChange={(e) => setSettingsForm({ ...settingsForm, cta_url: e.target.value })} className={inputCls} placeholder="https://wa.me/91..." />
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mb-1">Landing Page Photos</h2>
                  <p className="text-muted-foreground text-sm mb-6">The 'About / The Architect' section photo and the team group photo. Paste any public image URL.</p>
                  <label className={labelCls}>About section photo</label>
                  <input data-testid="settings-about-photo" value={settingsForm.about_photo} onChange={(e) => setSettingsForm({ ...settingsForm, about_photo: e.target.value })} className={`${inputCls} mb-4`} placeholder="https://..." />
                  {settingsForm.about_photo && (
                    <div className="mb-6 border border-border w-full max-w-xs aspect-[4/3] overflow-hidden">
                      <img src={settingsForm.about_photo} alt="About preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <label className={labelCls}>Team group photo URL</label>
                  <input data-testid="settings-group-photo" value={settingsForm.group_photo} onChange={(e) => setSettingsForm({ ...settingsForm, group_photo: e.target.value })} className={`${inputCls} mb-4`} placeholder="https://... (paste an image link)" />
                  <label className={labelCls}>Group photo caption</label>
                  <input data-testid="settings-group-caption" value={settingsForm.group_photo_caption} onChange={(e) => setSettingsForm({ ...settingsForm, group_photo_caption: e.target.value })} className={inputCls} />
                  {settingsForm.group_photo && (
                    <div className="mt-4 border border-border aspect-[21/9] overflow-hidden">
                      <img src={settingsForm.group_photo} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl mb-1">Social Media Links</h2>
                  <p className="text-muted-foreground text-sm mb-6">Paste full URLs. Empty fields are hidden from the site footer.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {["website", "whatsapp", "instagram", "youtube", "linkedin", "twitter", "facebook"].map((k) => (
                      <div key={k}>
                        <label className={labelCls}>{k}</label>
                        <input
                          data-testid={`settings-social-${k}`}
                          value={settingsForm.socials[k] || ""}
                          onChange={(e) => setSettingsForm({ ...settingsForm, socials: { ...settingsForm.socials, [k]: e.target.value } })}
                          className={inputCls}
                          placeholder={`https://...`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <button data-testid="settings-save-button" onClick={saveSettings} disabled={savingSettings} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display font-bold uppercase text-sm px-6 py-3 hover:bg-[#1AE39C] transition-colors disabled:opacity-60">
                  <SettingsIcon size={15} /> {savingSettings ? "Saving..." : "Save Settings"}
                </button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Post Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="post-dialog" className="bg-background border border-border rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight text-2xl">{editing ? "Edit Post" : "New Post"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Title</label>
              <input data-testid="post-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Category</label>
                <input data-testid="post-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Read time</label>
                <input data-testid="post-readtime-input" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Cover image URL</label>
              <input data-testid="post-cover-input" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={inputCls} placeholder="https://..." />
            </div>
            <div>
              <label className={labelCls}>Excerpt</label>
              <textarea data-testid="post-excerpt-input" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} className={`${inputCls} resize-y`} />
            </div>
            <div>
              <label className={labelCls}>Content (use blank lines for paragraphs)</label>
              <textarea data-testid="post-content-input" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} className={`${inputCls} resize-y`} />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="post-published-toggle" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#12C98A]" />
              <span className="font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Published</span>
            </label>
          </div>
          <DialogFooter>
            <button data-testid="post-cancel-button" onClick={() => setDialogOpen(false)} className="border border-border px-5 py-2.5 font-mono-accent text-xs uppercase tracking-[0.15em] hover:border-primary transition-colors">Cancel</button>
            <button data-testid="post-save-button" onClick={savePost} className="bg-primary text-primary-foreground font-display font-bold uppercase text-sm px-6 py-2.5 hover:bg-[#1AE39C] transition-colors">{editing ? "Update" : "Publish"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Member Dialog */}
      <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
        <DialogContent data-testid="member-dialog" className="bg-background border border-border rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight text-2xl">{editingMember ? "Edit Member" : "Add Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Name</label>
              <input data-testid="member-name-input" value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className={labelCls}>Role / Designation</label>
                <input data-testid="member-role-input" value={memberForm.role} onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })} className={inputCls} placeholder="Chief Investment Officer" />
              </div>
              <div>
                <label className={labelCls}>Order</label>
                <input data-testid="member-order-input" type="number" value={memberForm.order} onChange={(e) => setMemberForm({ ...memberForm, order: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Photo URL (optional)</label>
              <input data-testid="member-photo-input" value={memberForm.photo} onChange={(e) => setMemberForm({ ...memberForm, photo: e.target.value })} className={inputCls} placeholder="https://... (leave blank for initials)" />
            </div>
            <div>
              <label className={labelCls}>Bio</label>
              <textarea data-testid="member-bio-input" value={memberForm.bio} onChange={(e) => setMemberForm({ ...memberForm, bio: e.target.value })} rows={4} className={`${inputCls} resize-y`} />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="member-cancel-button" onClick={() => setMemberDialogOpen(false)} className="border border-border px-5 py-2.5 font-mono-accent text-xs uppercase tracking-[0.15em] hover:border-primary transition-colors">Cancel</button>
            <button data-testid="member-save-button" onClick={saveMember} className="bg-primary text-primary-foreground font-display font-bold uppercase text-sm px-6 py-2.5 hover:bg-[#1AE39C] transition-colors">{editingMember ? "Update" : "Add"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
